/**
 * One stop in a day panel. Resolves the referenced venue name client-side
 * via `trpc.venue.getById`; a missing/unpublished venue (D4 dangling stop)
 * renders the "this winery is no longer listed" placeholder, visually bridged
 * by the surrounding day panel (the leg pill is computed server-side to skip
 * it — D4).
 *
 * Carries the keyboard-reorder drag handle (a real <button>, ≥44×44,
 * aria-label="Reorder {stop name}") — Space/Enter grab, arrows move,
 * Esc cancel — plus edit/remove and the "Move to day…" affordances. Drag-drop
 * handlers are wired by the parent day panel.
 */

import { useEffect, useRef, useState } from 'react';
import type { SerializedStop } from './itineraryTypes';
import {
  computeVenueHoursWarning,
  type StopWarning,
  type VenueOperatingHours,
} from './stopTiming';
import { trpc } from '../../lib/trpc';
import { StopTimingStrip } from './StopTimingStrip';
import { StopWarningsList } from './StopWarningsList';
import { Caption, BodyText } from '../ui/Typography';
import { Button } from '../ui/Button';

interface ItineraryStopCardProps {
  stop: SerializedStop;
  /** 1-based position label for a11y announcements. */
  position: number;
  total: number;
  dayLabel: string;
  /** Calendar date the parent day is scheduled for (ISO YYYY-MM-DD), if any.
   * Required for the per-stop venue-hours check. */
  dayDate?: string;
  /** Timing warnings computed at the day level. Empty when none apply. */
  warnings?: StopWarning[];
  /** True when this stop is the active keyboard-grab target. */
  grabbed: boolean;
  readOnly?: boolean;
  onGrabToggle: () => void;
  onKeyboardMove: (e: React.KeyboardEvent) => void;
  onEditDetails: () => void;
  onRemove: () => void;
  /**
   * Other days this stop can be moved to (the current day is excluded by the
   * parent). Drives the "Move to day…" menu — the keyboard/power-user path to
   * a cross-day move, mirroring the drag-onto-tab gesture (#245). Empty or
   * omitted hides the affordance.
   */
  moveTargets?: { id: string; label: string }[];
  onMoveToDay?: (toDayId: string) => void;
  /** Native drag-drop wiring from the day panel. */
  draggableProps?: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
}

export const ItineraryStopCard = ({
  stop,
  position,
  total,
  dayLabel,
  dayDate,
  warnings = [],
  grabbed,
  readOnly = false,
  onGrabToggle,
  onKeyboardMove,
  onEditDetails,
  onRemove,
  moveTargets = [],
  onMoveToDay,
  draggableProps,
}: ItineraryStopCardProps) => {
  const [showRemove, setShowRemove] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const canMove = !readOnly && Boolean(onMoveToDay) && moveTargets.length > 0;
  const moveRowRef = useRef<HTMLDivElement>(null);

  // Send focus into the day list as it opens so the keyboard path (the whole
  // point of this menu) doesn't require tabbing back through the card.
  useEffect(() => {
    if (showMove) moveRowRef.current?.querySelector('button')?.focus();
  }, [showMove]);

  const isPlace = stop.refType === 'place';

  const venueQuery = trpc.venue.getById.useQuery(
    { id: stop.venueId ?? '' },
    {
      enabled: Boolean(stop.venueId) && !stop.isDangling,
      retry: false,
    }
  );

  // Place stops are self-contained snapshots — they never load a venue and can
  // never dangle. Only venue stops dangle (venue removed/unpublished).
  const isDangling =
    !isPlace &&
    (stop.isDangling || (Boolean(stop.venueId) && venueQuery.isError));
  const venueName = venueQuery.data?.name;
  const displayName = isPlace
    ? (stop.place?.name ?? 'Place')
    : (venueName ?? 'Winery');
  const heroUrl = isPlace
    ? undefined
    : venueQuery.data?.firstHeroImage?.cloudinaryUrl;
  const venueAddress = venueQuery.data?.address;
  // Venue-hours warnings are venue-only. Place hours are deliberately out of
  // scope, so a place stop gets cross-stop timing warnings only.
  const hoursWarning = isPlace
    ? null
    : computeVenueHoursWarning({
        arrivalTime: stop.arrivalTime,
        durationMinutes: stop.durationMinutes,
        dayDate,
        operatingHours:
          (venueQuery.data?.operatingHours as VenueOperatingHours | undefined) ??
          null,
        venueName,
      });
  const mergedWarnings = hoursWarning ? [...warnings, hoursWarning] : warnings;
  const locationLabel = isPlace
    ? (stop.place?.address ?? null)
    : venueAddress && (venueAddress.city || venueAddress.state)
      ? [venueAddress.city, venueAddress.state].filter(Boolean).join(', ')
      : null;

  return (
    <div
      className={`flex items-stretch gap-3 ${isDangling ? 'opacity-80' : ''}`}
      {...draggableProps}
      aria-roledescription="Reorderable stop"
    >
      {/* Keyboard + drag handle — outside the card, vertically centered.
          Real button, ≥44×44 (h-8 = 48px under the custom scale). */}
      {!readOnly && (
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 self-center items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-info-500"
          aria-label={`Reorder ${displayName}`}
          aria-pressed={grabbed}
          onClick={onGrabToggle}
          onKeyDown={onKeyboardMove}
        >
          <span aria-hidden="true">⠿</span>
        </button>
      )}

      <div
        className={`flex-1 overflow-hidden rounded-lg border bg-gray-50 transition-shadow motion-reduce:transition-none ${
          grabbed
            ? 'border-primary-700 ring-2 ring-info-500'
            : 'border-[#DBD4C9]'
        }`}
      >
        <div className="flex items-stretch h-40 sm:h-44">
          {/* Full-bleed hero (matches CompactVenueCard treatment). */}
          <div className="w-[40%] sm:w-[35%] shrink-0 overflow-hidden bg-stone-200">
            {heroUrl && !isDangling ? (
              <img
                src={heroUrl}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-stone-400">
                <svg
                  className="h-10 w-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  {isPlace ? (
                    <>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                    </>
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                    />
                  )}
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-1 min-w-0 items-stretch gap-3 p-3 sm:p-4">
            <div className="flex flex-1 min-w-0 flex-col justify-center">
              <Caption className="text-gray-500">
                Stop {position} of {total}
              </Caption>

              {isDangling ? (
                <div className="mt-1">
                  <BodyText className="text-gray-700">
                    This winery is no longer listed
                  </BodyText>
                  <Caption className="text-gray-500">
                    It was removed or unpublished. The route skips it.
                  </Caption>
                </div>
              ) : !isPlace && venueQuery.isLoading ? (
                <BodyText className="mt-1 text-gray-400">
                  Loading winery…
                </BodyText>
              ) : (
                <>
                  <h3 className="mt-1 font-serif text-lg sm:text-xl font-normal leading-tight text-gray-900 line-clamp-2">
                    {displayName}
                  </h3>
                  {locationLabel && (
                    <span
                      className={
                        isPlace
                          ? 'mt-1 block text-xs font-sans text-gray-500 line-clamp-1'
                          : 'mt-1 block text-[0.7rem] font-sans tracking-widest uppercase text-gray-500'
                      }
                    >
                      {locationLabel}
                    </span>
                  )}
                </>
              )}

              {/* Prominent timing strip — start + duration. */}
              <StopTimingStrip
                arrivalTime={stop.arrivalTime}
                durationMinutes={stop.durationMinutes}
                readOnly={readOnly}
                onEditDetails={onEditDetails}
              />
              {stop.notes && (
                <Caption className="mt-1 block truncate text-gray-600">
                  {stop.notes}
                </Caption>
              )}

              <StopWarningsList warnings={mergedWarnings} />
            </div>

            {!readOnly && (
              <div className="flex shrink-0 flex-col items-stretch justify-center gap-2">
                <Button size="sm" variant="outline" onClick={onEditDetails}>
                  Edit
                </Button>
                {canMove && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowRemove(false);
                      setShowMove((v) => !v);
                    }}
                    aria-expanded={showMove}
                    aria-label={`Move ${displayName} to another day`}
                  >
                    Move…
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowMove(false);
                    setShowRemove(true);
                  }}
                  aria-label={`Remove ${displayName}`}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>
        </div>

        {showMove && canMove && (
          // Inline row rather than a floating popover: the card clips overflow,
          // and this matches the remove-confirm affordance right below.
          <div
            ref={moveRowRef}
            className="flex flex-wrap items-center gap-2 border-t border-[#DBD4C9] px-3 py-2 sm:px-4"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setShowMove(false);
              }
            }}
          >
            <Caption className="mr-auto text-gray-600">Move to day…</Caption>
            {moveTargets.map((target) => (
              <Button
                key={target.id}
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowMove(false);
                  onMoveToDay?.(target.id);
                }}
              >
                {target.label}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setShowMove(false)}>
              Cancel
            </Button>
          </div>
        )}

        {showRemove && (
          <div className="flex items-center justify-end gap-2 border-t border-[#DBD4C9] px-3 py-2 sm:px-4">
            <Caption className="mr-auto text-gray-600">
              Remove this stop?
            </Caption>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowRemove(false)}
            >
              Keep
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setShowRemove(false);
                onRemove();
              }}
            >
              Remove
            </Button>
          </div>
        )}

        {/* a11y: announce the active grab state to the parent live region via
            aria; the parent ItineraryEditor owns the actual live region. */}
        {grabbed && (
          <Caption className="block border-t border-[#DBD4C9] px-3 py-2 text-primary-800 sm:px-4">
            Grabbed — use arrow keys to move, Enter to drop, Esc to cancel.
            Position {position} of {total}, {dayLabel}.
          </Caption>
        )}
      </div>
    </div>
  );
};
